import React, { useMemo } from "react";

/* Deterministic pseudo-random from a string seed (trail slug). */
function rng(seed) {
    let h = 2166136261;
    for (let i = 0; i < seed.length; i++) { h ^= seed.charCodeAt(i); h = Math.imul(h, 16777619); }
    return () => { h += 0x6D2B79F5; let t = Math.imul(h ^ (h >>> 15), 1 | h); t ^= t + Math.imul(t ^ (t >>> 7), 61 | t); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

function smoothPath(pts) {
    if (pts.length < 2) return "";
    let d = `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`;
    for (let i = 1; i < pts.length; i++) {
        const [x0, y0] = pts[i - 1]; const [x1, y1] = pts[i];
        const cx = (x0 + x1) / 2;
        d += ` C${cx.toFixed(1)},${y0.toFixed(1)} ${cx.toFixed(1)},${y1.toFixed(1)} ${x1.toFixed(1)},${y1.toFixed(1)}`;
    }
    return d;
}

/**
 * MapFragment — a small "torn from the field map" SVG:
 * irregular contour lines + one acid single-track with a direction arrow.
 * Decorative but consistent per trail (seeded by slug).
 */
export default function MapFragment({ seed = "argentario", className = "", animate = false }) {
    const { contours, trail, arrow } = useMemo(() => {
        const r = rng(seed);
        const W = 160, H = 100;
        const contours = [];
        for (let c = 0; c < 4; c++) {
            const pts = [];
            const baseY = 15 + c * 22 + r() * 6;
            for (let x = -10; x <= W + 10; x += 24) pts.push([x, baseY + (r() - 0.5) * 16]);
            contours.push(smoothPath(pts));
        }
        const tp = [];
        let y = 8 + r() * 10;
        for (let x = 6; x <= W - 10; x += 22) { y += 8 + r() * 10; tp.push([x, Math.min(H - 6, y)]); }
        const last = tp[tp.length - 1], prev = tp[tp.length - 2];
        const ang = Math.atan2(last[1] - prev[1], last[0] - prev[0]) * 180 / Math.PI;
        return { contours, trail: smoothPath(tp), arrow: { x: last[0], y: last[1], ang } };
    }, [seed]);

    return (
        <svg viewBox="0 0 160 100" className={className} aria-hidden="true" preserveAspectRatio="none">
            {contours.map((d, i) => (
                <path key={i} d={d} fill="none" stroke="#F4EEE0" strokeOpacity={0.16 - i * 0.02} strokeWidth="0.8" />
            ))}
            <path d={trail} fill="none" stroke="#C9FF3D" strokeWidth="1.6" strokeLinecap="round" className={animate ? "draw-line" : ""} />
            <g transform={`translate(${arrow.x},${arrow.y}) rotate(${arrow.ang})`}>
                <path d="M-4,-3 L2,0 L-4,3 Z" fill="#C9FF3D" />
            </g>
            <circle cx="6" cy={contours.length ? 12 : 12} r="1.6" fill="#F4EEE0" opacity="0.8" />
        </svg>
    );
}

/** Small elevation profile — descending line, seeded. */
export function ElevationProfile({ seed = "x", className = "" }) {
    const d = useMemo(() => {
        const r = rng(seed + "-elev");
        const pts = [];
        let y = 6;
        for (let x = 0; x <= 100; x += 10) { y = Math.min(30, y + 1.5 + r() * 3.5); pts.push([x, y]); }
        return smoothPath(pts);
    }, [seed]);
    return (
        <svg viewBox="0 0 100 32" className={className} aria-hidden="true" preserveAspectRatio="none">
            <path d={`${d} L100,32 L0,32 Z`} fill="#F4EEE0" fillOpacity="0.06" />
            <path d={d} fill="none" stroke="#C9FF3D" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
    );
}
